import { useFeatureGate } from "@statsig/react-bindings";
import { useCallback } from "react";

import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { CombinedNode } from "@/pages/scenario-builder/utils/nodes";
import { DefaultCategoryValue, DefaultProductValue } from "@/pages/scenario-builder/v3/utils";
import { BCPNode } from "@/pages/scenario-builder/v3/utils/bcp";

export const useDefaultNodeName = (selectedNode?: CombinedNode | null) => {
  const { nodes } = useInfo();
  const { value: isNewBehaviorEnabled } = useFeatureGate(featureFlag.scenarioPortfolioDropdown);

  const mapDefaultNodeName = (name: string) => {
    const node = selectedNode ?? nodes?.getDefaultNode();

    if (isNewBehaviorEnabled) {
      return name === "default" ? node?.warchestNode.extras.mappedName : name;
    }

    return name;
  };

  return { mapDefaultNodeName };
};

export const useDefaultNodeNameInfo = () => {
  const { nodes, initInfo, configData } = useInfo();
  const { value: isNewBehaviorEnabled } = useFeatureGate(featureFlag.scenarioPortfolioDropdown);
  const mapping = configData?.mapping?.meta ?? {};
  const node = nodes?.getDefaultNode();

  const mapBcpName = ({
    brand,
    category,
    product,
  }: {
    brand: string;
    category: string;
    product: string;
  }): BCPNode => {
    const defaultNodeName = initInfo.defaultNodeName ?? "";

    if (isNewBehaviorEnabled) {
      return {
        brand:
          brand === "default"
            ? (node?.warchestNode.extras.mappedName ?? defaultNodeName)
            : (mapping?.[brand] ?? brand),
        category: category !== "default" ? (mapping?.[category] ?? category) : category,
        product: product !== "default" ? (mapping?.[product] ?? product) : product,
      };
    }

    return {
      brand:
        brand === "default"
          ? (mapping?.[defaultNodeName] ?? defaultNodeName)
          : (mapping?.[brand] ?? brand),
      category: category !== "default" ? (mapping?.[category] ?? category) : category,
      product: product !== "default" ? (mapping?.[product] ?? product) : product,
    };
  };

  const mapDefaultNodeName = useCallback(
    (brand: string) => {
      if (isNewBehaviorEnabled) {
        return brand === "default" ? (node?.warchestNode.extras.mappedName ?? brand) : brand;
      }

      return brand === "default" ? (initInfo?.defaultNodeName ?? brand) : brand;
    },
    [nodes, initInfo, isNewBehaviorEnabled]
  );

  const getBCPHierarchyLabel = useCallback(
    (bcp: BCPNode) => {
      const mappedBcp = mapBcpName(bcp);

      if (bcp.product !== DefaultProductValue) {
        return mappedBcp.product;
      }

      if (bcp.category !== DefaultCategoryValue) {
        return mappedBcp.category;
      }

      return mappedBcp.brand;
    },
    [mapBcpName]
  );

  return { mapDefaultNodeName, mapBcpName, getBCPHierarchyLabel };
};
